// frontend/src/services/api.js
import axios from "axios";
import {
  getTenantFromUrl,
  getBaseApiUrl,
  getCurrentTenantCode,
  isCustomDomain,
} from "../utils/tenant";
import toast from "react-hot-toast";

// ✅ Get the base URL dynamically
const API_URL = getBaseApiUrl();

console.log(`🔧 API URL configured: ${API_URL}`);
console.log(`📍 Current hostname: ${window.location.hostname}`);
console.log(`📍 Is custom domain: ${isCustomDomain()}`);

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // ✅ Ensure URL has a leading slash
    if (config.url && !config.url.startsWith("/")) {
      config.url = "/" + config.url;
    }

    // Get tenant code
    let tenantCode = getCurrentTenantCode();

    // Always set the tenant header
    if (tenantCode) {
      config.headers["X-Tenant-Code"] = tenantCode;
    }

    // ✅ Also add tenant code as query param for GET requests
    if (config.method === "get" || config.method === "delete") {
      config.params = {
        ...config.params,
        tenantCode: tenantCode,
      };
    }

    if (process.env.NODE_ENV === "development") {
      console.log(
        `📤 ${config.method.toUpperCase()} ${config.baseURL}${config.url} (Tenant: ${tenantCode})`,
      );
    }

    return config;
  },
  (error) => {
    console.error("❌ Request interceptor error:", error);
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    const sessionExpiry = response.headers["x-session-expiry"];
    if (sessionExpiry) {
      localStorage.setItem("sessionExpiry", sessionExpiry);
    }

    // Handle successful account deletion
    if (
      response.config.url?.includes("/auth/account") &&
      response.status === 200
    ) {
      console.log("✅ Account deleted successfully, clearing all storage");

      localStorage.clear();
      sessionStorage.clear();

      document.cookie.split(";").forEach(function (c) {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });

      toast.success(
        response.data?.message || "Account deactivated successfully",
      );

      setTimeout(() => {
        window.location.replace("/login?t=" + Date.now());
      }, 500);
    }

    return response;
  },
  (error) => {
    // ✅ Handle CORS errors specifically
    if (
      error.message?.includes("Network Error") ||
      error.code === "ERR_NETWORK"
    ) {
      console.error("🌐 Network/CORS Error:", error);

      // Check if we're on a custom domain and suggest solution
      if (isCustomDomain()) {
        console.warn(
          `⚠️ You're on a custom domain (${window.location.hostname}). Make sure the backend accepts requests from this domain.`,
        );

        // Try to recover by using relative URLs
        if (error.config && error.config.url) {
          console.log("🔄 Retrying with relative URL...");
          // Remove baseURL and use relative path
          const relativeConfig = {
            ...error.config,
            baseURL: "",
            url: error.config.url,
          };
          return api.request(relativeConfig);
        }
      }
    }

    if (error.code === "ECONNABORTED") {
      return Promise.reject({
        message: "Request timeout - Server is taking too long to respond",
        status: 408,
        originalError: error,
      });
    }

    if (error.response) {
      console.error(
        `❌ API Error ${error.response.status}:`,
        error.response.data,
      );

      if (
        error.response.status === 403 &&
        error.response.data?.error === "TENANT_INACTIVE"
      ) {
        window.dispatchEvent(new CustomEvent("tenant-inactive"));
      }

      // Handle account deactivated error
      if (
        error.response.status === 401 &&
        (error.response.data?.accountDeactivated ||
          error.response.data?.message?.includes("deactivated"))
      ) {
        localStorage.clear();
        sessionStorage.clear();

        toast.error(
          error.response.data?.message || "Your account has been deactivated",
        );

        setTimeout(() => {
          window.location.replace("/login?t=" + Date.now());
        }, 1500);

        return Promise.reject(error);
      }

      // Handle authentication errors
      if (error.response.status === 401) {
        const requestUrl = error.config?.url || "";
        const isLoginRequest = requestUrl.includes("/auth/login");
        const errorCode = error.response.data?.error;
        const sessionExpired =
          errorCode === "SESSION_EXPIRED" ||
          errorCode === "TOKEN_EXPIRED" ||
          error.response.data?.sessionExpired ||
          error.response.data?.message
            ?.toLowerCase()
            .includes("session expired");

        if (!isLoginRequest && sessionExpired) {
          [
            "token",
            "user",
            "sessionToken",
            "sessionExpiry",
            "tokenExpiry",
          ].forEach((key) => localStorage.removeItem(key));
          window.dispatchEvent(
            new CustomEvent("auth-session-expired", {
              detail: {
                message: "Your session expired. Please sign in again.",
              },
            }),
          );
        }
      }

      // Handle account already deactivated
      if (
        error.response.status === 400 &&
        error.response.data?.message === "Account is already deactivated"
      ) {
        localStorage.clear();
        sessionStorage.clear();

        toast.info("Your account was already deactivated");

        setTimeout(() => {
          window.location.replace("/login?t=" + Date.now());
        }, 1000);

        return Promise.reject(error);
      }

      // ✅ Handle CORS errors from server
      if (error.response.status === 0 || error.response.status === 503) {
        console.error("🔒 CORS or Server error:", error.response);
        toast.error("Unable to connect to server. Please refresh the page.");
      }
    } else if (error.request) {
      console.error("📡 No response received:", error.request);

      // Check if it's a CORS issue
      const isCorsError =
        error.message?.includes("CORS") ||
        error.message?.includes("cross-origin");

      if (isCorsError) {
        console.error("🔒 CORS Error detected!");
        toast.error(
          `Unable to connect to ${window.location.hostname}. Please ensure the backend is configured correctly.`,
          { duration: 6000 },
        );
      } else {
        return Promise.reject({
          message:
            "Cannot connect to server. Please check if the backend is running.",
          status: 503,
          originalError: error,
        });
      }
    }

    return Promise.reject(error);
  },
);

// ✅ Helper function to handle CORS errors gracefully
export const handleCorsError = (error) => {
  if (
    error.message?.includes("Network Error") ||
    error.code === "ERR_NETWORK"
  ) {
    console.log("🌐 Network error detected. Attempting recovery...");

    // If we're on a custom domain, try using the current domain
    if (isCustomDomain()) {
      const currentOrigin = window.location.origin;
      console.log(`🔄 Retrying with current origin: ${currentOrigin}`);

      // Update the API base URL to current origin
      api.defaults.baseURL = currentOrigin;

      // Return a promise that resolves with the retry
      return api;
    }
  }
  return null;
};

// ✅ Export the API instance
export default api;
